import React, { useState, useEffect, useRef } from "react";
import { freight } from "../lib/freightApi.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { Link } from "react-router-dom";
import {
  LANGUAGES, TRAILERS, ZONES, ZONE_TZ, QUIET, BLANK_DRIVER, DRIVER_CONSENT_TEXT,
  driversFormFromApi, driverFormToApi, driverErrorText, driverPhoneClash,
} from "./driverForm.js";

/* --------------------------------- tokens ---------------------------------- */

const T = {
  ink: "#000000", ink2: "#5A5A5A", ink3: "#8C8C8C", ink4: "#BFBFBF",
  hair: "#DCDCDC", hairSoft: "#EDEDED", wash: "#F5F5F5",
};
const sans = `-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Inter, system-ui, sans-serif`;
const mono = `ui-monospace, "SF Mono", SFMono-Regular, Menlo, monospace`;

const FMCSA = {
  legalName: "Bayou City Carriers LLC", dba: "Bayou City Trucking",
  usdot: "3184427", mc: "MC-118934", entity: "Carrier", status: "Active",
  address: "4411 Navigation Blvd, Houston, TX 77011",
  powerUnits: 8, driversOnFile: 9, safetyRating: "Satisfactory",
  insurance: "BIPD on file — $1,000,000", mcs150: "Updated Feb 2026",
  registeredEmail: "o•••••@bayoucitycarriers.com", registeredPhone: "(713) •••-0155",
};

// LANGUAGES, TRAILERS, ZONES, ZONE_TZ and QUIET live in driverForm.js, beside the code that
// turns them into what the backend stores.
const ROLES = ["Dispatch", "After hours", "Billing and advances", "Operations manager", "Safety"];

// ⛔ THE THREE NUMBERS THAT USED TO BE HERE DID NOT EXIST. (713) 999-0140, (281) 999-0177 and
// (844) 999-0102 were invented for the mockup, and whichever one a carrier picked was saved as
// their caller-id — so every call the platform placed would have been refused, correctly, with
// from_number_not_found, at dial time, looking like a platform fault. The picker now asks the
// platform which lines are real (GET /api/carrier/lines → core GET /v1/freight/lines) and shows
// nothing when there are none.
const fmtLine = (e164) => {
  const d = String(e164 || "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : String(e164 || "");
};
const lineLabel = (l) => (l.type === "tollfree" ? "Toll free" : "Local");

const CSV_SAMPLE = [
  { name: "Ivan Petrov", phone: "(713) 555-0128", language: "English", truckNo: "T-145", trailerType: "53' dry van", trailerNo: "V-8842", zone: "Central", quiet: "9:00p – 6:00a", ecName: "Lena Petrova", ecPhone: "(713) 555-0129", consent: true, truckDesc: "", note: "" },
  { name: "Andre Simmons", phone: "(832) 555-0193", language: "English", truckNo: "T-160", trailerType: "48' flatbed", trailerNo: "", zone: "Central", quiet: "9:00p – 6:00a", ecName: "Tanya Simmons", ecPhone: "(832) 555-0194", consent: true, truckDesc: "", note: "" },
  { name: "Luz Herrera", phone: "(956) 555-0111", language: "English and Spanish", truckNo: "T-172", trailerType: "53' reefer", trailerNo: "R-2210", zone: "Central", quiet: "10:00p – 5:00a", ecName: "Mateo Herrera", ecPhone: "(956) 555-0112", consent: true, truckDesc: "", note: "" },
];

/* ------------------------------- primitives -------------------------------- */

function Micro({ children, style }) {
  return <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: 0.9, textTransform: "uppercase", color: T.ink3, ...style }}>{children}</div>;
}

function Tag({ children, solid }) {
  return (
    <span style={{
      fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase",
      border: `1px solid ${T.ink}`, background: solid ? T.ink : "transparent", color: solid ? "#fff" : T.ink,
      borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function Soft({ children }) {
  return <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase", border: `1px solid ${T.hair}`, color: T.ink2, borderRadius: 3, padding: "1px 5px", whiteSpace: "nowrap" }}>{children}</span>;
}

function Button({ children, onClick, kind = "primary", disabled, size = "md" }) {
  const k = {
    primary: { background: T.ink, color: "#fff", border: `1px solid ${T.ink}` },
    quiet: { background: "#fff", color: T.ink, border: `1px solid ${T.hair}` },
  }[kind];
  return (
    <button onClick={onClick} disabled={disabled} className="alf-btn" style={{
      ...k, font: "inherit", fontSize: size === "sm" ? 12.5 : 13.5, fontWeight: 500, letterSpacing: -0.15,
      padding: size === "sm" ? "6px 11px" : "9px 16px", borderRadius: 7,
      cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1,
    }}>{children}</button>
  );
}

const inputBase = {
  width: "100%", font: "inherit", fontSize: 14, color: T.ink, background: "#fff",
  border: `1px solid ${T.hair}`, borderRadius: 7, padding: "9px 11px", outline: "none", boxSizing: "border-box",
};

function Field({ label, value, onChange, placeholder, isMono, hint, half, third, children }) {
  return (
    <div style={{ flex: third ? "1 1 30%" : half ? "1 1 46%" : "1 1 100%", minWidth: 0 }}>
      <Micro style={{ marginBottom: 5 }}>{label}</Micro>
      {children || <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={{ ...inputBase, fontFamily: isMono ? mono : sans }} />}
      {hint && <div style={{ fontSize: 12, color: T.ink3, marginTop: 5, lineHeight: 1.45 }}>{hint}</div>}
    </div>
  );
}

function Select({ value, onChange, options }) {
  // A stored value outside the list (an imported "Spanish only", or nothing at all) is shown as
  // itself. Without this the control drew the FIRST option while holding another value, so the
  // screen said "53' dry van" about a driver whose trailer nobody ever entered.
  const extra = !options.includes(value);
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={{ ...inputBase, appearance: "none", cursor: "pointer" }}>
      {extra && <option value={value ?? ""}>{value || "— not set —"}</option>}
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  );
}

function Row({ label, value, isMono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "7px 0" }}>
      <span style={{ fontSize: 13, color: T.ink3 }}>{label}</span>
      <span style={{ fontSize: 13, textAlign: "right", fontFamily: isMono ? mono : sans, fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}

function Check({ on, onChange, label, note }) {
  return (
    <button onClick={() => onChange(!on)} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", font: "inherit", width: "100%" }}>
      <span style={{
        width: 17, height: 17, borderRadius: 4, flexShrink: 0, marginTop: 1, boxSizing: "border-box",
        border: `1px solid ${on ? T.ink : T.hair}`, background: on ? T.ink : "#fff", color: "#fff",
        fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center",
      }}>{on ? "✓" : ""}</span>
      <span>
        <span style={{ display: "block", fontSize: 13.5, lineHeight: 1.45 }}>{label}</span>
        {note && <span style={{ display: "block", fontSize: 12, color: T.ink3, marginTop: 2, lineHeight: 1.45 }}>{note}</span>}
      </span>
    </button>
  );
}

function Waveform({ active }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2.5, height: 14 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={active ? "alf-bar" : ""} style={{ width: 2, borderRadius: 1, background: T.ink, height: active ? 14 : 4, animationDelay: `${i * 0.11}s`, opacity: active ? 1 : 0.3 }} />
      ))}
    </span>
  );
}

function Steps({ list, current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
      {list.map((l, i) => (
        <React.Fragment key={l}>
          <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{
              width: 17, height: 17, borderRadius: 999, fontSize: 9.5, fontFamily: mono, boxSizing: "border-box",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: i < current ? T.ink : "#fff", color: i < current ? "#fff" : i === current ? T.ink : T.ink4,
              border: `${i === current ? 2 : 1}px solid ${i <= current ? T.ink : T.hair}`,
            }}>{i < current ? "✓" : i + 1}</span>
            <span style={{ fontSize: 12.5, fontWeight: i === current ? 600 : 400, color: i <= current ? T.ink : T.ink3 }}>{l}</span>
          </span>
          {i < list.length - 1 && <span style={{ width: 18, height: 1, background: T.hair }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function Head({ title, note }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.6 }}>{title}</div>
      {note && <div style={{ fontSize: 13.5, color: T.ink2, marginTop: 5, lineHeight: 1.55, maxWidth: 520 }}>{note}</div>}
    </div>
  );
}

function Progress({ lines, at }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "4px 0" }}>
      {lines.map((s, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, opacity: i <= at ? 1 : 0.25 }}>
          {i < at ? <span style={{ width: 16, fontSize: 13 }}>✓</span> : i === at ? <span style={{ width: 16 }}><Waveform active /></span> : <span style={{ width: 16, textAlign: "center", color: T.ink4 }}>·</span>}
          <span style={{ fontSize: 13.5 }}>{s}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ step 1: carrier ---------------------------- */

// Exported for App.render.test.mjs. The FMCSA result card renders off a `carrier` object
// built from the lookup response, and every defect it has had needed that object populated
// — which the route-level render cannot reach, since the step starts at phase "idle".
export function CarrierStep({ carrier, setCarrier, onNext, saving }) {
  // Whose attestation it is. An attestation with no name attached is just a checkbox.
  const { email } = useAuth();
  // Real lines, from the platform. See the note on fmtLine above for why this is not a constant.
  const [lines, setLines] = useState({ loading: true, configured: null, list: [] });
  useEffect(() => {
    let alive = true;
    freight.listLines()
      .then((r) => alive && setLines({ loading: false, configured: r?.configured ?? true, list: r?.lines ?? [] }))
      .catch(() => alive && setLines({ loading: false, configured: null, list: [] }));
    return () => { alive = false; };
  }, []);
  const [mode, setMode] = useState("mc");
  const [num, setNum] = useState("");
  const [phase, setPhase] = useState(carrier.found ? (carrier.verified ? (carrier.callNumber ? "number" : "number") : "verify") : "idle");
  const [line, setLine] = useState(0);
  const [code, setCode] = useState("");
  const [attested, setAttested] = useState(false);
  const [sentTo, setSentTo] = useState("email");
  const LOOKUP = ["Querying FMCSA", "Checking operating authority", "Reading insurance on file"];

  const [lookupErr, setLookupErr] = useState(null);

  // A REAL lookup. This used to be a 420 ms timer that returned the same hard-coded
  // carrier for every number, so a wrong MC looked exactly like a right one. The
  // platform owns the FMCSA key and fails closed; when nobody has connected it yet the
  // server says configured:false and we let the operator type the details rather than
  // invent them.
  useEffect(() => {
    if (phase !== "looking") return;
    let cancelled = false;
    const ticker = setInterval(() => setLine((n) => (n < LOOKUP.length ? n + 1 : n)), 480);
    (async () => {
      try {
        const r = await freight.lookup(mode === "mc" ? { mc: num } : { dot: num });
        if (cancelled) return;
        if (r.configured === false) {
          setLookupErr(r.note || "Lookup is not connected yet — enter your details below.");
          setCarrier({ ...carrier, found: true, manual: true, lookedUpBy: mode, entered: num,
                       legalName: "", mc: mode === "mc" ? num : "", dot: mode === "dot" ? num : "" });
        } else if (!r.found) {
          setLookupErr(`No carrier found for ${mode === "mc" ? "MC" : "USDOT"} ${num}. Check the number.`);
          // Back to the search box. This went to an "enter" phase that nothing renders a
          // control for, so a mistyped number left the screen with no button but a reload.
          setPhase("idle");
          return;
        } else {
          if (!r.authorized) setLookupErr(`FMCSA shows this authority as ${r.status.replace(/_/g, " ")}.`);
          setCarrier({ ...carrier, found: true, manual: false, lookedUpBy: mode, entered: num,
                       legalName: r.carrierName || "", mc: r.mcNumber || "", dot: r.dotNumber || "",
                       safetyRating: r.safetyRating, authorized: r.authorized,
                       // Kept so the card can say what FMCSA actually reports, and whether
                       // the platform's key is connected at all.
                       authorityStatus: r.status || null, mock: !!r.mock });
        }
        setPhase("found");
      } catch (err) {
        if (cancelled) return;
        setLookupErr("Could not reach the lookup. Enter your details below.");
        setCarrier({ ...carrier, found: true, manual: true, lookedUpBy: mode, entered: num });
        setPhase("found");
      }
    })();
    return () => { cancelled = true; clearInterval(ticker); };
  }, [phase]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Start with your authority"
        note="Enter your MC or USDOT number and we'll pull the rest from FMCSA. Newer authorities are DOT-only, and FMCSA is phasing MC numbers out — either works." />

      {/* lookupErr was set on every failure path and drawn nowhere: "not found", "not
          connected" and "could not reach the lookup" all looked like nothing happening. */}
      {lookupErr && (
        <div role="alert" style={{ fontSize: 13, color: T.ink, padding: "10px 12px", border: `1px solid ${T.ink}`, borderRadius: 8, maxWidth: 560 }}>
          {lookupErr}
        </div>
      )}

      {(phase === "idle" || phase === "looking") && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 400 }}>
          <div style={{ display: "flex", border: `1px solid ${T.hair}`, borderRadius: 7, overflow: "hidden", width: "fit-content" }}>
            {[["mc", "MC number"], ["dot", "USDOT number"]].map(([v, l]) => (
              <button key={v} onClick={() => setMode(v)} style={{
                font: "inherit", fontSize: 12.5, fontWeight: 500, padding: "6px 12px", border: "none", cursor: "pointer",
                background: mode === v ? T.ink : "#fff", color: mode === v ? "#fff" : T.ink2,
              }}>{l}</button>
            ))}
          </div>
          <Field label={mode === "mc" ? "MC number" : "USDOT number"} value={num} onChange={setNum} placeholder={mode === "mc" ? "MC-118934" : "3184427"} isMono />
          {phase === "idle"
            ? <div><Button disabled={!num.trim()} onClick={() => { setLookupErr(null); setLine(0); setPhase("looking"); }}>Look it up</Button></div>
            : <Progress lines={LOOKUP} at={line} />}
        </div>
      )}

      {/*
        ⛔ THIS CARD USED TO RENDER SEVEN FIELDS THE LOOKUP DOES NOT RETURN.

        It showed "Doing business as", "Physical address", "Power units", "Drivers on file",
        "Insurance" and "MCS-150" — all real FMCSA fields, none of them in the response.
        `/api/fmcsa/lookup` returns carrierName, mcNumber, dotNumber, safetyRating,
        authorized and status, and nothing else. They came from the days when this screen
        was a 420ms timer returning one hard-coded carrier, and when the lookup became real
        the rows stayed behind reading off keys that no longer existed: an empty black pill
        where the status went, "USDOT " with nothing after it, and — because two of them were
        wrapped in String() — two rows that literally read "undefined".

        So the card shows what came back. Bringing those rows back means returning the fields
        from core's /internal/freight/verify first; a row is not a placeholder to fill in later.
      */}
      {phase !== "idle" && phase !== "looking" && (
        <div className="alf-fade" style={{ border: `1px solid ${T.ink}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.hairSoft}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.35 }}>
              {carrier.legalName || (carrier.manual ? "Enter your details below" : "No name on the record")}
            </span>
            {carrier.authorityStatus && <Tag solid>{String(carrier.authorityStatus).replace(/_/g, " ")}</Tag>}
            <span style={{ marginLeft: "auto", fontFamily: mono, fontSize: 12, color: T.ink3 }}>
              {[carrier.mc && `MC ${carrier.mc}`, carrier.dot && `USDOT ${carrier.dot}`].filter(Boolean).join(" · ")}
            </span>
          </div>
          {/* ⛔ "ENTER YOUR DETAILS BELOW" HAD NOTHING BELOW IT. With the lookup unconnected there
              was no name to type, so the carrier saved as "My Carrier" and Alfred opened every
              call with "it's Alfred with My Carrier dispatch". */}
          {carrier.manual && (
            <div style={{ padding: "12px 18px 0" }}>
              <Field label="Company name" value={carrier.legalName || ""} onChange={(v) => setCarrier({ ...carrier, legalName: v })}
                placeholder="Required. Alfred says this name on every call." />
            </div>
          )}
          <div style={{ padding: "12px 18px 16px" }}>
            <Row label="Legal name on the FMCSA record" value={carrier.manual ? "Not checked" : (carrier.legalName || "—")} />
            <Row label="Operating authority" value={
              carrier.manual ? "Not checked — lookup is not connected"
                : carrier.authorized === true ? "Authorized"
                : carrier.authorized === false ? `Not authorized${carrier.authorityStatus ? ` — ${String(carrier.authorityStatus).replace(/_/g, " ")}` : ""}`
                : "Unverifiable"} />
            <Row label="Safety rating" value={carrier.safetyRating || "None on file"} />
          </div>
          {/*
            ⛔ THE PLATFORM FALLS BACK TO A MOCK WHEN IT HAS NO FMCSA KEY, and that mock is
            deterministic and realistic on purpose — it derives a plausible carrier name and
            safety rating from the number you typed. Unlabelled, a demo or a real operator
            reads an invented carrier as a confirmed one, which is precisely how somebody
            onboards under an authority that is not theirs.
          */}
          {carrier.mock && (
            <div style={{ padding: "11px 18px", borderTop: `1px solid ${T.ink}`, background: T.ink, color: "#fff", fontSize: 12.5, lineHeight: 1.5 }}>
              <b>This is not a real FMCSA result.</b> The platform has no FMCSA key connected,
              so it generated a plausible-looking carrier from the number you entered. Nothing
              here has been checked against the register.
            </div>
          )}
        </div>
      )}

      {phase === "found" && (
        <div className="alf-fade" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button disabled={carrier.manual && !String(carrier.legalName || "").trim()}
            onClick={() => { setLookupErr(null); setPhase("verify"); }}>That's us</Button>
          <Button kind="quiet" onClick={() => { setLookupErr(null); setPhase("idle"); setCarrier({ ...carrier, found: false }); }}>Not us, search again</Button>
        </div>
      )}

      {phase === "verify" && (
        <div className="alf-fade" style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 14, maxWidth: 480 }}>
          {/*
            ⛔ THIS STEP USED TO FAKE A VERIFICATION. It offered to send a six-digit code to
            the contact FMCSA has on file — "not one you type in here" — and then accepted
            ANY six digits. Its own hint said so: "Any six digits will work in this
            prototype". Nothing was sent and nothing was checked.

            That is worse than having no step. A screen claiming to have confirmed an
            authority against the FMCSA record is exactly what a hijacker needs it to say,
            and the carrier onboarded under somebody else's MC looks identical to a real one
            forever after.

            Until there is a real channel to the registered contact, this is an ATTESTATION:
            the operator states, on the record, that the authority is theirs. That is a
            genuine control — it creates an accountable claim with a name attached — and it
            does not pretend to be a verification it is not.
          */}
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Confirm the authority is yours</div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5 }}>
              MC hijacking is common, so this is on the record. We cannot yet send a code to the
              contact FMCSA holds for this authority — when we can, this step will use it. For now
              you are attesting that {carrier.legalName || "this carrier"} is yours to act for.
            </div>
          </div>
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, lineHeight: 1.5, cursor: "pointer" }}>
            <input type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              I am authorised to act for this carrier, and I understand Alfred will place calls
              to its drivers using its identity.
            </span>
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={!attested} onClick={() => { setCarrier({ ...carrier, verified: false, attestedAt: new Date().toISOString(), attestedBy: email || null, authorities: [{ name: carrier.legalName, mc: carrier.mc, attested: true }] }); setPhase("number"); }}>Continue</Button>
            <Button kind="quiet" onClick={() => setPhase("found")}>Back</Button>
          </div>
        </div>
      )}

      {phase === "number" && (
        <div className="alf-fade" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>The number Alfred calls from</div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4, lineHeight: 1.5, maxWidth: 520 }}>
              We provision this number to your verified authority and register it for STIR/SHAKEN attestation. Unregistered numbers get labeled "Spam Likely" by the carriers, and a driver who doesn't recognize the number doesn't answer.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
              {lines.loading && <div style={{ fontSize: 12.5, color: T.ink3 }}>Checking which lines are available…</div>}
              {!lines.loading && lines.list.length === 0 && (
                <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, border: `1px dashed ${T.hair}`, borderRadius: 8, padding: "11px 14px" }}>
                  {lines.configured === false
                    ? "The voice platform is not connected to this dashboard yet, so there is no number to call from. Nothing will dial until it is."
                    : "This account has no active line yet. A number has to be provisioned to your authority before Alfred can call a driver."}
                </div>
              )}
              {lines.list.map((l) => (
                <button key={l.phoneNumber} onClick={() => setCarrier({ ...carrier, callNumber: l.phoneNumber })} style={{
                  display: "flex", alignItems: "center", gap: 12, textAlign: "left", font: "inherit", cursor: "pointer",
                  padding: "11px 14px", borderRadius: 8, background: "#fff",
                  border: `1px solid ${carrier.callNumber === l.phoneNumber ? T.ink : T.hair}`,
                }}>
                  <span style={{ fontFamily: mono, fontSize: 14 }}>{fmtLine(l.phoneNumber)}</span>
                  <span style={{ fontSize: 12.5, color: T.ink3 }}>{lineLabel(l)}</span>
                  {carrier.callNumber === l.phoneNumber && <span style={{ marginLeft: "auto" }}><Tag solid>selected</Tag></span>}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 }}>
              <Field half label="Caller ID name" value={carrier.callerId} onChange={(v) => setCarrier({ ...carrier, callerId: v })} placeholder={carrier.legalName || "Your carrier's name"}
                hint="Registered to CNAM so it shows as a name, not a number." />
              <Field half label="Office phone" value={carrier.officePhone} onChange={(v) => setCarrier({ ...carrier, officePhone: v })} placeholder="(713) 555-0170" isMono
                hint="Where Alfred tells people to call back." />
            </div>
          </div>

          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Office details</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
              <Field half label="Office time zone">
                <Select value={carrier.officeZone} onChange={(v) => setCarrier({ ...carrier, officeZone: v })} options={ZONES} />
              </Field>
              <Field half label="Who holds the certificate of insurance" value={carrier.coiHolder} onChange={(v) => setCarrier({ ...carrier, coiHolder: v })}
                placeholder="Name or agency" hint="Brokers ask for a current COI constantly. Alfred routes those asks here instead of guessing." />
            </div>
            <div style={{ fontSize: 12, color: T.ink3, marginTop: 10, lineHeight: 1.45 }}>
              Call plans are scheduled in office time and checked against each driver's home zone before Alfred dials.
            </div>
          </div>

          <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>Authorities</span>
              <Soft>{carrier.authorities.length} on file</Soft>
            </div>
            {carrier.authorities.map((a) => (
              <div key={a.mc} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: `1px solid ${T.hairSoft}`, marginTop: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13.5, fontWeight: 500, minWidth: 180 }}>{a.name}</span>
                <span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink2 }}>{a.mc}</span>
                {a.verified && <Soft>verified</Soft>}
              </div>
            ))}
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 12, lineHeight: 1.5 }}>
              Running more than one authority — a second DBA, a leased-on entity — means each gets its own verification and its own calling number. Drivers can be assigned to either.
            </div>
            <div style={{ marginTop: 12 }}>
              <Button size="sm" kind="quiet" onClick={() => setCarrier({ ...carrier, authorities: [...carrier.authorities, { name: "Second authority (unverified)", mc: "MC-000000", verified: false }] })}>
                Add another authority
              </Button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={!carrier.callNumber} onClick={onNext}>Add drivers</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ step 2: drivers ---------------------------- */

// BLANK_DRIVER lives in driverForm.js with the mapping to the backend's columns.

function DriverForm({ value, onChange, onSave, onCancel, saveLabel, busy }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const ready = value.name.trim() && value.phone.trim() && value.truckNo.trim() && value.ecName.trim() && value.ecPhone.trim() && value.consent;
  return (
    <div className="alf-fade" style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <Field half label="Driver name" value={value.name} onChange={set("name")} placeholder="Required" />
        <Field half label="Cell phone" value={value.phone} onChange={set("phone")} placeholder="Required" isMono />
        <Field half label="Language"><Select value={value.language} onChange={set("language")} options={LANGUAGES} /></Field>
        <Field half label="Truck number" value={value.truckNo} onChange={set("truckNo")} placeholder="T-118" isMono />
      </div>

      <div>
        <Micro style={{ marginBottom: 8 }}>Equipment</Micro>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <Field half label="Truck make and model" value={value.truckDesc} onChange={set("truckDesc")} placeholder="Optional — 2021 Cascadia" />
          <Field half label="Trailer type"><Select value={value.trailerType} onChange={set("trailerType")} options={TRAILERS} /></Field>
          <Field half label="Trailer number" value={value.trailerNo} onChange={set("trailerNo")} placeholder="Optional — blank for drop and hook" isMono />
        </div>
      </div>

      <div>
        <Micro style={{ marginBottom: 8 }}>When Alfred can call</Micro>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <Field half label="Home time zone"><Select value={value.zone} onChange={set("zone")} options={ZONES} /></Field>
          <Field half label="Quiet hours"><Select value={value.quiet} onChange={set("quiet")} options={QUIET} /></Field>
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>
          Call times are scheduled in your office time zone and checked against this one. A 6:30a Central call is 4:30a for a Pacific driver — Alfred holds it.
        </div>
      </div>

      <div>
        <Micro style={{ marginBottom: 8 }}>Emergency contact</Micro>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <Field half label="Name" value={value.ecName} onChange={set("ecName")} placeholder="Required" />
          <Field half label="Phone" value={value.ecPhone} onChange={set("ecPhone")} placeholder="Required" isMono />
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>
          Last rung on the escalation ladder. Only used when a driver is unreachable across repeated attempts, and only by a person — never by Alfred.
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${T.hairSoft}`, paddingTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
        <Micro>Consent</Micro>
        <Check on={value.consent} onChange={set("consent")}
          label={DRIVER_CONSENT_TEXT}
          note="Required. Your drivers cross into California, Illinois, Florida, Pennsylvania and Washington — all two-party-consent states. Alfred discloses on every call regardless, but you need the acknowledgment on file." />
      </div>

      <Field label="Anything Alfred should know" value={value.note} onChange={set("note")} placeholder="Optional — best time to call, hard of hearing, speaks little English" />

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Button disabled={!ready || busy} onClick={onSave}>{busy ? "Saving…" : saveLabel}</Button>
        {onCancel && <Button kind="quiet" onClick={onCancel}>Cancel</Button>}
        {!ready && <span style={{ fontSize: 12, color: T.ink3 }}>Name, cell, truck number, emergency contact, and the consent box are required.</span>}
      </div>
    </div>
  );
}

function DriversStep({ carrier, drivers, setDrivers, onBack, onNext }) {
  const [draft, setDraft] = useState(BLANK_DRIVER);
  const [adding, setAdding] = useState(drivers.length === 0);
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState(BLANK_DRIVER);
  const [importing, setImporting] = useState(null);
  const fileRef = useRef(null);

  const [importErr, setImportErr] = useState(null);
  const [driverErr, setDriverErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // The list on screen is always what the server holds, never what this tab typed.
  async function reload() {
    setDrivers(driversFormFromApi(await freight.listDrivers()));
  }

  /**
   * ⛔ ADD, EDIT AND THE CONSENT BOX USED TO CHANGE REACT STATE ONLY. A driver typed in here
   * vanished on refresh, and an imported driver could never be given consent, so Alfred could
   * never call them. Now every save goes to the server and the list is re-read from it.
   *
   * The backend keys a new driver on (carrier, phone), so ADDING one with another driver's number
   * would quietly overwrite THAT driver. Refused here instead (driverPhoneClash). An edit goes
   * through PATCH, which changes a number in place, so the driver keeps its loads and call
   * history, and answers 409 when someone else, removed drivers included, holds the number.
   */
  async function saveDriver(form, existing) {
    setDriverErr(null);
    const clash = driverPhoneClash(drivers, form.phone, existing?.id);
    if (clash) {
      setDriverErr(`${clash.name} already has that number. Edit ${clash.name} instead.`);
      return false;
    }
    setBusy(true);
    try {
      const body = driverFormToApi(form);
      if (existing) await freight.updateDriver(existing.id, body);
      else await freight.saveDriver(body);
      await reload();
      return true;
    } catch (err) {
      setDriverErr(driverErrorText(err));
      return false;
    } finally { setBusy(false); }
  }

  async function removeDriver(d) {
    setDriverErr(null); setBusy(true);
    try {
      await freight.removeDriver(d.id);
      await reload();
      if (editId === d.id) setEditId(null);
    } catch (err) {
      setDriverErr(driverErrorText(err));
    } finally { setBusy(false); }
  }

  // ⛔ READ THE FILE THE OPERATOR PICKED. This function used to ignore it entirely and
  // load three fictional drivers, so somebody uploading forty real ones saw three
  // strangers and no error. The server parses and reports every rejected row by line.
  async function onFile(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setImportErr(null); setBusy(true);
    try {
      const csv = await f.text();
      const r = await freight.previewDrivers(csv);
      setImporting({ name: f.name, csv, rows: r.rows || [], errors: r.errors || [] });
      if (!r.rows?.length) setImportErr((r.errors || []).map((x) => x.message).join(" ") || "Nothing in that file looked like a driver row.");
    } catch (err) {
      setImportErr(err.body?.error === "no_carrier" ? "Save your carrier details first." : (err.message || "Could not read that file."));
    } finally {
      setBusy(false);
      e.target.value = "";   // let the same file be picked again after a fix
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Who's driving?"
        note="Alfred needs a name, cell, truck number, and an emergency contact before it can call anyone. Trailer details are optional." />
      {/*
        ⛔ "FMCSA lists N drivers for X. You've added M." is gone. `driversOnFile` was never
        in the lookup response, so `> 0` was always false and the line never rendered — a
        comparison waiting on a field nobody returns. It comes back when
        /internal/freight/verify returns a driver count, not before.
      */}

      {importing && (
        <div className="alf-fade" style={{ border: `1px solid ${T.ink}`, borderRadius: 10, padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, fontWeight: 600, letterSpacing: -0.3 }}>{importing.rows.length} drivers found in {importing.name}</span>
            <Soft>preview</Soft>
          </div>
          <div style={{ marginTop: 12 }}>
            {/* The preview rows are the server's parse (snake_case), not the form's shape. Read
                as truckNo/phone they drew a blank phone and truck for every row. */}
            {importing.rows.map((r) => (
              <div key={r.phone_e164} style={{ display: "flex", gap: 12, padding: "8px 0", borderTop: `1px solid ${T.hairSoft}`, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 13.5, fontWeight: 500, minWidth: 140 }}>{r.name}</span>
                <span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink2 }}>{fmtLine(r.phone_e164)}</span>
                <span style={{ fontSize: 12.5, color: T.ink3 }}>{[r.truck_no, r.trailer_type].filter(Boolean).join(" · ") || "no truck on file"}</span>
                <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  {!r.consent && <Soft>no consent in file</Soft>}
                  {!r.ec_name && <Soft>no emergency contact</Soft>}
                </span>
              </div>
            ))}
          </div>
          {/* [].every() is true, so an empty file used to say "Every row says the driver agreed". */}
          {importing.rows.length > 0 && <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 12, lineHeight: 1.5 }}>
            {importing.rows.every((r) => r.consent)
              ? "Every row says the driver agreed to automated calls. Importing records that, as your confirmation that each one was told."
              : `${importing.rows.filter((r) => !r.consent).length} driver(s) come in without consent on file. Alfred won't call them until you open each one, tick the consent box and save — that's a legal requirement, not a formality.`}
          </div>}
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <Button disabled={busy || !importing.rows.length} onClick={async () => {
              setBusy(true); setImportErr(null);
              try {
                const r = await freight.importDrivers(importing.csv, "Imported from the carrier's roster; the dispatcher confirmed each driver was told.");
                await reload();
                setImporting(null); setAdding(false);
                if (r.errors?.length) setImportErr(`Imported ${r.imported}. ${r.errors.length} row(s) skipped — see the file.`);
              } catch (err) {
                setImportErr(err.message || "Import failed.");
              } finally { setBusy(false); }
            }}>{busy ? "Importing…" : `Import ${importing.rows.length} drivers`}</Button>
            <Button kind="quiet" onClick={() => setImporting(null)}>Discard</Button>
          </div>
          {/* Every rejected row, with its line number. A short import that says nothing is
              how a dispatcher discovers on Tuesday that six drivers were never added. */}
          {importing.errors?.length > 0 && (
            <div style={{ marginTop: 12, padding: "10px 12px", border: `1px solid ${T.hair}`, borderRadius: 8, background: T.wash }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>{importing.errors.length} row(s) will not import</div>
              {importing.errors.slice(0, 8).map((e, i) => (
                <div key={i} style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5 }}>Line {e.line}: {e.message}</div>
              ))}
              {importing.errors.length > 8 && <div style={{ fontSize: 12.5, color: T.ink3 }}>…and {importing.errors.length - 8} more.</div>}
            </div>
          )}
        </div>
      )}

      {/* importErr was set on every failure path and drawn nowhere, so a failed import, a
          "save your carrier first" and a short import all looked like nothing happening. */}
      {(importErr || driverErr) && (
        <div role="alert" style={{ fontSize: 13, color: T.ink, padding: "10px 12px", border: `1px solid ${T.ink}`, borderRadius: 8 }}>
          {driverErr || importErr}
        </div>
      )}

      {drivers.length > 0 && (
        <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
          {drivers.map((d, i) => (
            <div key={d.id} style={{ borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", flexWrap: "wrap" }}>
                <span style={{ width: 30, height: 30, borderRadius: 999, border: `1px solid ${T.hair}`, fontSize: 11.5, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {d.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                </span>
                <span style={{ flex: 1, minWidth: 160 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{d.name}</span>
                    {!d.consent && <Tag>consent needed</Tag>}
                  </span>
                  <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2 }}>
                    <span style={{ fontFamily: mono }}>{d.phone}</span> · {d.truckNo} · {d.trailerType}{d.trailerNo ? ` ${d.trailerNo}` : ""} · {d.zone}
                  </span>
                </span>
                <button onClick={() => { setEditId(editId === d.id ? null : d.id); setEditDraft(d); }}
                  style={{ font: "inherit", fontSize: 12.5, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>
                  {editId === d.id ? "Close" : d.consent ? "Edit" : "Finish"}
                </button>
                <button disabled={busy} onClick={() => removeDriver(d)} style={{ font: "inherit", fontSize: 12.5, color: T.ink3, background: "none", border: "none", cursor: busy ? "default" : "pointer" }}>Remove</button>
              </div>
              {editId === d.id && (
                <div style={{ padding: "0 18px 18px" }}>
                  <DriverForm value={editDraft} onChange={setEditDraft} saveLabel="Save changes" busy={busy}
                    onSave={async () => { if (await saveDriver(editDraft, d)) setEditId(null); }}
                    onCancel={() => setEditId(null)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <DriverForm value={draft} onChange={setDraft} saveLabel="Add driver" busy={busy}
          onSave={async () => { if (await saveDriver(draft, null)) { setDraft(BLANK_DRIVER); setAdding(false); } }}
          onCancel={drivers.length ? () => { setAdding(false); setDraft(BLANK_DRIVER); } : null} />
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Button kind="quiet" onClick={() => setAdding(true)}>Add another driver</Button>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>or</span>
          <button onClick={() => fileRef.current && fileRef.current.click()} style={{ font: "inherit", fontSize: 13, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}>
            upload a driver list
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.txt" onChange={onFile} style={{ display: "none" }} />
        </div>
      )}

      <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 18 }}>
        <Button disabled={drivers.length === 0} onClick={onNext}>Next: contacts</Button>
        <Button kind="quiet" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}

/* ----------------------------- step 3: contacts ---------------------------- */

// ⛔ AN EMAIL, BECAUSE THE BACKEND IDENTIFIES A DISPATCHER BY ONE. `addDispatcher` rejects a
// row without a valid address (carrier.mjs), so a contact form that collected only a name and
// a phone could never be saved — and this step was not saving. The whole list, with hours and
// permissions, was React state that a refresh threw away while the Review screen listed it as
// though it were on file.
const BLANK_CONTACT = { name: "", email: "", role: "Dispatch", phone: "", hours: "Mon–Fri, 6:00a–6:00p", afterHours: false, can: ["ops"] };

function ContactsStep({ contacts, setContacts, onBack, onNext }) {
  const [draft, setDraft] = useState(BLANK_CONTACT);
  const [adding, setAdding] = useState(contacts.length === 0);
  // The email is required because the server rejects a dispatcher without one — better to
  // refuse here than to let somebody fill the form in and lose it on save.
  const ready = draft.name.trim() && draft.phone.trim()
    && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(draft.email.trim());
  const hasAfterHours = contacts.some((c) => c.afterHours);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Who's on your dispatch team?"
        note="The people Alfred works for. Each load names one as its contact, and they see every call Alfred places on the board. Alfred does not phone or transfer to them yet." />

      {contacts.length > 0 && (
        <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
          {contacts.map((c, i) => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}`, flexWrap: "wrap" }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 16 }}>{i + 1}</span>
              <span style={{ flex: 1, minWidth: 160 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14.5, fontWeight: 500, letterSpacing: -0.2 }}>{c.name}</span>
                  <Soft>{c.role}</Soft>
                  {c.afterHours && <Tag>after hours</Tag>}
                  {c.can.map((k) => <Soft key={k}>{k}</Soft>)}
                </span>
                <span style={{ display: "block", fontSize: 12.5, color: T.ink3, marginTop: 2 }}>
                  <span style={{ fontFamily: mono }}>{c.phone}</span> · {c.hours}
                </span>
              </span>
              <button onClick={() => setContacts(contacts.filter((x) => x.id !== c.id))} style={{ font: "inherit", fontSize: 12.5, color: T.ink3, background: "none", border: "none", cursor: "pointer" }}>Remove</button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div className="alf-fade" style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <Field half label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="Required" />
            <Field half label="Work email" value={draft.email} onChange={(v) => setDraft({ ...draft, email: v })} placeholder="Required — this is how they're stored" isMono />
            <Field half label="Phone" value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} placeholder="Required" isMono />
            <Field half label="Role"><Select value={draft.role} onChange={(v) => setDraft({ ...draft, role: v })} options={ROLES} /></Field>
            <Field half label="Available hours" value={draft.hours} onChange={(v) => setDraft({ ...draft, hours: v })} placeholder="Mon–Fri, 6:00a–6:00p" />
          </div>
          <div>
            <Micro style={{ marginBottom: 7 }}>What they can clear</Micro>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[["ops", "Ops and appointments"], ["billing", "Money and advances"], ["safety", "Incidents and claims"]].map(([k, l]) => {
                const on = draft.can.includes(k);
                return (
                  <button key={k} onClick={() => setDraft({ ...draft, can: on ? draft.can.filter((x) => x !== k) : [...draft.can, k] })}
                    style={{ font: "inherit", fontSize: 12.5, padding: "6px 11px", borderRadius: 7, cursor: "pointer",
                      border: `1px solid ${on ? T.ink : T.hair}`, background: on ? T.ink : "#fff", color: on ? "#fff" : T.ink2 }}>{l}</button>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: T.ink3, marginTop: 7, lineHeight: 1.45 }}>
              This decides what they are allowed to clear on the board — a dispatcher can reset an appointment without being able to approve an advance. It does not make Alfred call them; nothing in this app dials anyone but a driver.
            </div>
          </div>
          <Check on={draft.afterHours} onChange={(v) => setDraft({ ...draft, afterHours: v })}
            label="This is the after-hours fallback"
            note="Gets the handoff when nobody else is on shift. Freight doesn't stop at 6pm — if no one holds this, overnight handoffs sit until morning." />
          <div style={{ display: "flex", gap: 8 }}>
            <Button disabled={!ready} onClick={() => { setContacts([...contacts, { ...draft, id: "s" + Math.random().toString(36).slice(2, 7) }]); setDraft(BLANK_CONTACT); setAdding(false); }}>Add contact</Button>
            {contacts.length > 0 && <Button kind="quiet" onClick={() => { setAdding(false); setDraft(BLANK_CONTACT); }}>Cancel</Button>}
          </div>
        </div>
      ) : (
        <Button kind="quiet" onClick={() => setAdding(true)}>Add another contact</Button>
      )}

      {/*
        ⛔ AN ESCALATION LADDER WAS DRAWN HERE AND NOTHING CLIMBS IT.
        It showed four timed rungs — primary, second contact at +20m, operations manager at
        +45m, the driver's emergency contact at +90m — above the sentence "A handoff climbs
        this ladder until someone picks it up." The identical diagram was deleted from the
        dispatch board in the same review that found this one, and this copy was worse: the
        +45m rung read a REAL operator's name off the list above it, so the screen named a
        person and told the operator that person would be called.
        The only outbound path in this app is placeDriverCall → the driver's own number.
        It comes back when something escalates.
      */}
      {contacts.length > 0 && (
        <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "14px 18px" }}>
          <Micro style={{ marginBottom: 8 }}>What these people are for</Micro>
          <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.55 }}>
            They are who the board offers when Alfred stops and a handoff needs a person, and
            the seat picker on the dispatch board is this list. Alfred does not ring them —
            it rings drivers — so somebody has to be watching the board.
            {hasAfterHours
              ? " Your after-hours contact is marked, so it is clear who holds the night."
              : " Nobody is marked as the after-hours fallback, so an overnight handoff has no obvious owner."}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, borderTop: `1px solid ${T.hairSoft}`, paddingTop: 18 }}>
        <Button disabled={contacts.length === 0} onClick={onNext}>Review</Button>
        <Button kind="quiet" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}

/* ------------------------------ step 4: review ----------------------------- */

function ReviewStep({ carrier, drivers, contacts, onBack, onFinish, done }) {
  const missingConsent = drivers.filter((d) => !d.consent);

  if (done) {
    return (
      <div className="alf-fade" style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "flex-start", paddingTop: 8 }}>
        <Waveform active />
        {/* `dba` was never set by the real lookup, so this read " is set up" with nothing in front of it. */}
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.6 }}>{carrier.legalName || "Your carrier"} is set up</div>
        <div style={{ fontSize: 13.5, color: T.ink2, lineHeight: 1.55, maxWidth: 480 }}>
          {drivers.length} driver{drivers.length === 1 ? "" : "s"} reachable from {carrier.callNumber}, {contacts.length} contact{contacts.length === 1 ? "" : "s"} on the ladder.
        </div>
        <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden", width: "100%", maxWidth: 480 }}>
          {[["Add your first load", "Upload a rate confirmation. Alfred reads the stops and builds the call plan around them."],
            ["Connect your ELD", "Motive or Samsara. Without it Alfred can't tell whether a driver is rolling, and it won't call anyone in motion."],
            ["Connect your TMS", "Optional. With it, load numbers resolve on their own."]].map(([t, d], i) => (
            <div key={t} style={{ display: "flex", gap: 12, padding: "13px 16px", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}` }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: T.ink4, minWidth: 18, paddingTop: 2 }}>{String(i + 1).padStart(2, "0")}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, letterSpacing: -0.2 }}>{t}</div>
                <div style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 3 }}>{d}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={onFinish}>Add your first load</Button>
          <Button kind="quiet" onClick={onFinish}>Connect an ELD</Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <Head title="Check it over" note="All of this stays editable later." />

      {missingConsent.length > 0 && (
        <div style={{ border: `1px solid ${T.ink}`, borderRadius: 10, padding: "13px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            <Tag solid>hold</Tag>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{missingConsent.length} driver{missingConsent.length === 1 ? "" : "s"} without consent on file</span>
          </div>
          <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 6, lineHeight: 1.5 }}>
            {missingConsent.map((d) => d.name).join(", ")} — Alfred won't dial them until you confirm they've been told it's an AI and that calls are recorded. Everything else about them is saved.
          </div>
        </div>
      )}

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, padding: "14px 18px 16px" }}>
        <Micro style={{ marginBottom: 6 }}>Carrier</Micro>
        <Row label={carrier.legalName || "Legal name"} value={carrier.mc ? `MC ${carrier.mc}` : "Not set"} isMono />
        {/* `usdot` never existed; the lookup writes `dot`. */}
        <Row label="USDOT" value={carrier.dot || "Not set"} isMono />
        {/*
          ⛔ THIS ROW SAID "Yes, by code to the FMCSA contact". No code is sent to anybody —
          that step was a fake verification that accepted any six digits, and it was replaced
          with an on-the-record attestation. The row has to describe the control that exists.
        */}
        {/* ⛔ "ON FILE" ONLY WHEN THE SERVER SAID SO. `savedAttestedAt` is what came back from
            POST /carrier; `attestedAt` alone is a checkbox ticked in this tab a moment ago.
            Claiming a record that is not stored is how this row read before. */}
        <Row label="Authority" value={carrier.savedAttestedAt
          ? `Attested on file by ${carrier.savedAttestedBy || "the operator"}, ${new Date(carrier.savedAttestedAt).toLocaleDateString()}`
          : carrier.attestedAt
            ? "Attested in this session — saved when you press Finish"
            : "Not attested"} />
        {/* ⛔ AND WHETHER ANY OF THIS WAS TYPED BY A PERSON. `scripts/seed-demo-carrier.mjs`
            writes a whole carrier — name, docket, caller-id, roster, loads — for a
            demonstration, and the only thing distinguishing it from an operator's own setup is
            `carriers.demo`, which until now nothing read. An operator who inherits a seeded
            account would otherwise be reviewing somebody else's fiction as their own record. */}
        {carrier.demo && <Row label="This account"
          value="Seeded for a demonstration by scripts/seed-demo-carrier.mjs — the name, docket, caller ID and roster were written by that script, not by you." />}
        <Row label="Calling from" value={carrier.callNumber || "Not set"} isMono />
        <Row label="Caller ID" value={carrier.callerId || carrier.legalName || "Not set"} />
        <Row label="Callback number" value={carrier.officePhone || "Not set"} isMono />
        <Row label="Office time zone" value={carrier.officeZone} />
        <Row label="COI held by" value={carrier.coiHolder || "Not set"} />
        <Row label="Authorities" value={`${carrier.authorities.length} on file`} />
      </div>

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.hairSoft}` }}><Micro>{drivers.length} driver{drivers.length === 1 ? "" : "s"}</Micro></div>
        {drivers.map((d, i) => (
          <div key={d.id} style={{ display: "flex", gap: 12, padding: "12px 18px", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}`, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 500, minWidth: 140 }}>{d.name}</span>
            <span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink2 }}>{d.phone}</span>
            {!d.consent && <Tag>consent needed</Tag>}
            <span style={{ fontSize: 12.5, color: T.ink3, marginLeft: "auto" }}>{d.truckNo} · {d.zone} · quiet {d.quiet}</span>
          </div>
        ))}
      </div>

      <div style={{ border: `1px solid ${T.hair}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${T.hairSoft}` }}><Micro>{contacts.length} contact{contacts.length === 1 ? "" : "s"}</Micro></div>
        {contacts.map((c, i) => (
          <div key={c.id} style={{ display: "flex", gap: 12, padding: "12px 18px", borderTop: i === 0 ? "none" : `1px solid ${T.hairSoft}`, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 500, minWidth: 140 }}>{c.name}</span>
            <span style={{ fontFamily: mono, fontSize: 12.5, color: T.ink2 }}>{c.phone}</span>
            {c.afterHours && <Tag>after hours</Tag>}
            <span style={{ fontSize: 12.5, color: T.ink3, marginLeft: "auto" }}>{c.role} · {c.hours}</span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Button onClick={onFinish}>Finish setup</Button>
        <Button kind="quiet" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}

/* ----------------------------------- app ----------------------------------- */

/* ─────────────────────────────────────────────────────────────────────────────
   THE BANNER HAS TO BE TRUE, AND IT STOPPED BEING TRUE.
   ⛔ This screen said "Sample data — not connected to live calls, and not saved" long after
   it had been wired up. It saves the carrier, looks the authority up at FMCSA, previews and
   imports a driver CSV, and asks the platform which caller-id lines actually exist. Brian,
   2026-09-22: "I'm not seeing the changes that were made" — he was looking at a banner
   telling him his real work was a mock, on the one carrier screen that is not.

   A screen that looks live and isn't is the failure this platform keeps paying for. A screen
   that IS live and says it isn't costs the same thing twice: the work gets redone, and the
   next real warning gets believed a little less. So the banner now reports what this screen
   actually does, and stays loud about what it still does not.
   ───────────────────────────────────────────────────────────────────────────── */
/**
 * ⛔ THIS BANNER SAID "Placing calls needs the platform key set on the server" ALWAYS.
 *
 * Static copy, written when the dial seam did not exist. The moment CORE_URL and
 * CORE_FREIGHT_API_KEY were set and /api/health flipped to calls:"enabled", the one line
 * on screen that talks about calling was still telling the operator calling was not
 * configured — while the Call button on the next screen would place a real call to a real
 * driver's mobile. A banner that cannot be wrong about that is worth the fetch.
 *
 * `calls` comes from the server's own health, so it says what is actually true rather than
 * what somebody remembered to edit. While it is still loading it claims nothing.
 */
function LiveBanner() {
  const [calls, setCalls] = useState(null);   // null = unknown, don't claim either way
  useEffect(() => {
    let alive = true;
    fetch('/api/health')
      .then((r) => r.json())
      .then((h) => alive && setCalls(h?.calls === 'enabled'))
      .catch(() => alive && setCalls(null));
    return () => { alive = false; };
  }, []);

  return (
    <div style={{
      background: "#12201e", color: "#fff", fontFamily: sans, fontSize: 12.5,
      padding: "7px 22px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
    }}>
      <span style={{
        fontFamily: mono, fontSize: 9.5, letterSpacing: 0.7, textTransform: "uppercase",
        border: "1px solid #fff", borderRadius: 3, padding: "1px 5px"
      }}>live</span>
      <span>
        Carrier setup. This saves — the carrier, the drivers you import, and the line
        Alfred calls from.{" "}
        {calls === true
          ? "Calling is connected: Alfred will place REAL calls to the drivers you import."
          : calls === false
            ? "Calling is not connected yet, so no call can be placed."
            : ""}
      </span>
      <Link to="/carrier" style={{ marginLeft: "auto", color: "#fff", fontSize: 12.5 }}>← Home</Link>
    </div>
  );
}

export default function AlfredOnboarding() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [carrier, setCarrier] = useState({ mc: "", found: false, verified: false, callNumber: "", officePhone: "", callerId: "", officeZone: "Central", coiHolder: "", authorities: [] });
  const [drivers, setDrivers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const LIST = ["Carrier", "Drivers", "Contacts", "Review"];

  // Setup is resumable. Coming back to this screen used to start from an empty object
  // every time, because nothing was stored anywhere; now it shows what is already saved.
  useEffect(() => {
    (async () => {
      try {
        const c = await freight.getCarrier();
        if (c) {
          setCarrier((prev) => ({ ...prev, found: true, saved: true, legalName: c.name, mc: c.mcNumber || "",
                                  dot: c.dotNumber || "",
                                  // From the stored timezone: this kept the default, so going back
                                  // through the carrier step re-saved a Denver carrier as Chicago.
                                  officeZone: Object.keys(ZONE_TZ).find((z) => ZONE_TZ[z] === c.timezone) || prev.officeZone,
                                  callNumber: c.phone || "",
                                  // What the SERVER holds, kept apart from what this tab typed.
                                  savedAttestedAt: c.attestedAt || null, savedAttestedBy: c.attestedBy || null,
                                  // ⛔ A SEEDED ACCOUNT SAYS SO ON THE SCREEN THAT REVIEWS IT.
                                  // `carriers.demo` existed from the first version of this
                                  // table and nothing ever read it. `seed-demo-carrier.mjs`
                                  // sets it — it is what stops that script adopting a real
                                  // operator's carrier — so this is where an operator finds
                                  // out the name, docket and roster in front of them were
                                  // written for a demonstration rather than by them.
                                  demo: c.demo === true }));
          // In the form's shape: the API's `truck`/`quietStart` read as truckNo/quiet drew blanks.
          setDrivers(driversFormFromApi(await freight.listDrivers()));
          // ⛔ A CARRIER ON FILE RESUMES AT DRIVERS. CarrierStep picks its phase once, at mount,
          // before this load returns, so a returning operator always met an empty lookup. Adding
          // one driver meant redoing the lookup, the attestation and the line, and "Add drivers"
          // then saved whatever that lookup said: a real lookup renamed the carrier, an
          // unconnected one saved it as "My Carrier". Back from Drivers still reaches the
          // carrier step, now with the stored name, docket, zone and line.
          setStep((s) => (s === 0 ? 1 : s));
        }
      } catch { /* not signed in yet, or a cold server: the screen still works as a fresh setup */ }
    })();
  }, []);

  // ⛔ THE CARRIER MUST EXIST BEFORE A DRIVER CAN. Drivers are stored against a carrier
  // id, so importing a roster before this ran answers no_carrier. Saving on the way out
  // of step 0 is what makes the import on step 1 possible at all.
  /**
   * ⛔ FINISHING USED TO BE `setDone(true)` AND NOTHING ELSE.
   *
   * The contacts step collected names, emails, phones, hours and permissions into React
   * state, the Review screen listed them as though they were on file, and a refresh threw the
   * lot away. `POST /carrier/dispatchers` existed the whole time and this screen never called
   * it — the same shape of defect as the brokerage onboarding that "succeeded" into an
   * in-memory object.
   *
   * So Finish writes them, and a failure does NOT show the done screen: a setup that says it
   * finished while the server has none of it is worse than one that says it could not.
   */
  async function finishSetup() {
    setSaveErr(null);
    setSaving(true);
    const failed = [];
    try {
      for (const c of contacts) {
        try {
          await freight.addDispatcher({
            email: c.email, name: c.name, phone: c.phone, role: c.role,
          });
        } catch (err) {
          failed.push(`${c.name || c.email}: ${err?.body?.message || err?.body?.error || err?.message || 'unknown error'}`);
        }
      }
    } finally {
      setSaving(false);
    }
    if (failed.length) {
      setSaveErr(`${failed.length === contacts.length ? 'None of your contacts' : `${failed.length} of your contacts`} could be saved — ${failed.join('; ')}. Nothing else was lost; fix the address and press Finish again.`);
      return;
    }
    setDone(true);
  }

  async function saveCarrierAndAdvance() {
    // The name is checked at "That's us", but the field stays editable after it; clearing it
    // there still saved "My Carrier", which Alfred then says on every call.
    if (carrier.manual && !String(carrier.legalName || "").trim()) {
      setSaveErr("Enter the company name first. Alfred says it on every call.");
      return;
    }
    setSaving(true); setSaveErr(null);
    try {
      const saved = await freight.saveCarrier({
        name: carrier.legalName || carrier.dba || "My Carrier",
        mcNumber: carrier.mc || (carrier.lookedUpBy === "mc" ? carrier.entered : ""),
        dotNumber: carrier.dot || (carrier.lookedUpBy === "dot" ? carrier.entered : ""),
        timezone: ZONE_TZ[carrier.officeZone] || "America/Chicago",
        // ⛔ NO FALLBACK TO THE OFFICE PHONE. This is the caller-id the platform dials from
        // and it must be a line the platform owns. The office number is where Alfred tells
        // people to call back — a different thing, and using it here was the last remaining
        // way for a number the platform does not own to become the caller-id.
        phone: carrier.callNumber || "",
        // The attester's name comes from the session server-side; sending the moment is enough.
        attestedAt: carrier.attestedAt || null
      });
      // ⛔ TAKE THE ATTESTATION BACK FROM THE SERVER, don't assume ours landed. An older
      // backend without the columns echoes nothing, and the review row then correctly says
      // "attested in this session" rather than claiming a record on file.
      setCarrier((prev) => ({ ...prev, saved: true, id: saved.id,
        savedAttestedAt: saved.attestedAt || null, savedAttestedBy: saved.attestedBy || null }));
      setStep(1);
    } catch (err) {
      setSaveErr(err.message || "Could not save the carrier. Try again.");
    } finally { setSaving(false); }
  }

  return (
    <div className="alf-root" style={{ fontFamily: sans, background: T.wash, color: T.ink, minHeight: "100vh", WebkitFontSmoothing: "antialiased" }}>
      <style>{`
        .alf-bar { animation: alfBar 1s ease-in-out infinite; transform-origin: bottom; }
        @keyframes alfBar { 0%,100% { transform: scaleY(.28);} 50% { transform: scaleY(1);} }
        .alf-fade { animation: alfFade .26s cubic-bezier(.4,0,.2,1); }
        @keyframes alfFade { from { opacity:0; transform: translateY(4px);} to { opacity:1; transform:none;} }
        .alf-btn { transition: opacity .15s, transform .1s; }
        .alf-btn:active { transform: scale(.975); }
        .alf-root input::placeholder { color:${T.ink4}; }
        .alf-root input:focus, .alf-root select:focus { border-color:#000 !important; }
        .alf-root button:focus-visible, .alf-root input:focus-visible, .alf-root select:focus-visible { outline:2px solid #000; outline-offset:2px; }
        @media (prefers-reduced-motion: reduce) { .alf-bar,.alf-fade { animation:none !important; } }
      `}</style>

      <LiveBanner />


      <header style={{ borderBottom: `1px solid ${T.hair}`, background: "#fff" }}>
        <div style={{ maxWidth: 780, margin: "0 auto", padding: "0 22px", height: 54, display: "flex", alignItems: "center", gap: 20 }}>
          <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: -0.4 }}>Alfred</span>
          <span style={{ fontSize: 12.5, color: T.ink3 }}>Setup</span>
        </div>
      </header>

      <main style={{ maxWidth: 780, margin: "0 auto", padding: "26px 22px 70px" }}>
        <div style={{ marginBottom: 24 }}><Steps list={LIST} current={done ? 4 : step} /></div>
        <div style={{ background: "#fff", border: `1px solid ${T.hair}`, borderRadius: 10, padding: "26px 26px 28px" }}>
          {step === 0 && <CarrierStep carrier={carrier} setCarrier={setCarrier} onNext={saveCarrierAndAdvance} saving={saving} />}
          {step === 1 && <DriversStep carrier={carrier} drivers={drivers} setDrivers={setDrivers} onBack={() => setStep(0)} onNext={() => setStep(2)} />}
          {step === 2 && <ContactsStep contacts={contacts} setContacts={setContacts} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
          {step === 3 && <ReviewStep carrier={carrier} drivers={drivers} contacts={contacts} done={done} onBack={() => setStep(2)} onFinish={finishSetup} />}
          {saveErr && (
            <div style={{ marginTop: 14, fontSize: 13, color: "#9b1c1c" }}>{saveErr}</div>
          )}
        </div>
      </main>
    </div>
  );
}
